import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { motion } from 'framer-motion';
import { mockData } from '../data/mock';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.15 } })
};

const Contact = () => {
  const { contact } = mockData;
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', service: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast({ title: "Demande envoyée !", description: "Nous vous recontacterons dans les plus brefs délais." });
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        throw new Error('Erreur');
      }
    } catch {
      toast({ title: "Erreur", description: "Veuillez réessayer ou nous appeler directement.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactDetails = [
    { icon: MapPin, label: "Adresse", value: contact.address },
    { icon: Phone, label: "Téléphone", value: contact.phone, href: "tel:0149100388" },
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}` },
    { icon: Clock, label: "Horaires", value: `${contact.hours.weekdays}\n${contact.hours.weekend}` }
  ];

  return (
    <section id="contact" className="py-24 sm:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
          {/* Left — Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={0}
          >
            <span 
              className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Contact
            </span>
            <h2 
              className="mt-4 text-4xl sm:text-5xl tracking-tight leading-tight font-normal text-[#1C1917]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Contactez-nous
            </h2>
            <p 
              className="mt-4 text-lg leading-relaxed text-[#57534E] max-w-md"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
            >
              Nous sommes à votre écoute pour toute demande de retouche ou conseil personnalisé.
            </p>

            <div className="mt-10 space-y-6">
              {contactDetails.map((item, i) => {
                const Icon = item.icon;
                const Wrapper = item.href ? 'a' : 'div';
                return (
                  <motion.div
                    key={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i + 1}
                  >
                    <Wrapper 
                      {...(item.href ? { href: item.href } : {})}
                      className="flex items-start gap-4 group"
                      data-testid={`contact-info-${item.label.toLowerCase()}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors">
                        <Icon className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <div className="text-xs tracking-[0.1em] uppercase text-[#A8A29E] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {item.label}
                        </div>
                        <div className="text-[#1C1917] whitespace-pre-line leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {item.value}
                        </div>
                      </div>
                    </Wrapper>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={1}
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-[0.1em] uppercase text-[#A8A29E] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Nom complet *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-[#F5F5F0] border-[#E8D5D1] focus-visible:ring-[#D4AF37] rounded-lg py-5"
                    placeholder="Votre nom"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.1em] uppercase text-[#A8A29E] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Téléphone
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="bg-[#F5F5F0] border-[#E8D5D1] focus-visible:ring-[#D4AF37] rounded-lg py-5"
                    placeholder="Votre téléphone"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-[0.1em] uppercase text-[#A8A29E] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Email *
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="bg-[#F5F5F0] border-[#E8D5D1] focus-visible:ring-[#D4AF37] rounded-lg py-5"
                  placeholder="votre@email.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-xs tracking-[0.1em] uppercase text-[#A8A29E] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Type de service
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="w-full px-3 py-3 bg-[#F5F5F0] border border-[#E8D5D1] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-[#1C1917]"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="contact-service-select"
                >
                  <option value="">Sélectionner un service</option>
                  <option value="vetements">Retouches de vêtements</option>
                  <option value="rideaux">Retouches de rideaux</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-xs tracking-[0.1em] uppercase text-[#A8A29E] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Votre message *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="bg-[#F5F5F0] border-[#E8D5D1] focus-visible:ring-[#D4AF37] rounded-lg"
                  placeholder="Décrivez votre demande..."
                  data-testid="contact-message-textarea"
                />
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-white font-medium py-6 rounded-full text-base tracking-wide shadow-lg hover:shadow-xl transition-all duration-300"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Envoi en cours...' : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" />
                    Envoyer ma demande
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
